/**
 * UserFormWizard.jsx
 * Formulario de Usuario em Etapas Premium
 *
 * 5 Passos:
 * 1. Basico - Nome, Email, Senha (obrigatorio)
 * 2. Contato - Telefone, Endereco (opcional)
 * 3. Permissoes - Admin, Limites (opcional)
 * 4. Atributos - Atributos personalizados (opcional)
 * 5. Revisao - Confirmar dados
 */

import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
 Box, Typography, Grid, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem 
} from '@mui/material';
import {
  Person as PersonIcon,
  ContactPhone as ContactIcon,
  Security as SecurityIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { sessionActions } from '../../store';
import { useAdministrator, useManager } from '../../common/util/permissions';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput, { PasswordInput } from '../../common/components/premium/PremiumInput';
import PremiumPhoneInput from '../../common/components/premium/PremiumPhoneInput';
import EditAttributesAccordion from './EditAttributesAccordion';
import useUserAttributes from '../../common/attributes/useUserAttributes';
import useCommonUserAttributes from '../../common/attributes/useCommonUserAttributes';

// ============================================================================
// COLORS HELPER - Centraliza cores com tokens e fallbacks
// ============================================================================

const getWizardColors = (tokens) => ({
  accent: tokens?.accent?.primary || '#00d4aa',
  accentSubtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
  accentHover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
  textPrimary: tokens?.text?.primary || '#f0f6fc',
  textSecondary: tokens?.text?.secondary || '#8b949e',
  textMuted: tokens?.text?.muted || '#6e7681',
  surfaceHover: tokens?.surface?.hover || 'rgba(255, 255, 255, 0.05)',
  borderSubtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
  success: tokens?.status?.success || tokens?.accent?.primary || '#00d4aa',
  error: tokens?.status?.error || tokens?.semantic?.error || '#f85149',
});

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            placeholder="Nome completo do usuario"
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={`${t('userEmail')} *`}
            value={data.email || ''}
            onChange={(e) => updateData({ email: e.target.value })}
            error={!!errors?.email}
            helperText={errors?.email}
            placeholder="email@exemplo.com"
            type="email"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PasswordInput
            label={data.id ? t('userPassword') : `${t('userPassword')} *`}
            onChange={(e) => updateData({ password: e.target.value })}
            error={!!errors?.password}
            helperText={errors?.password || (data.id ? t('wizardPasswordHint') : '')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumPhoneInput
            label={t('sharedPhone')}
            value={data.phone || ''}
            onChange={(e) => updateData({ phone: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: CONTATO E ENDERECO
// ============================================================================

const ContactStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const handleFormField = (key, value) => {
    updateData({
      attributes: {
        ...data.attributes,
        form: {
          ...data.attributes?.form,
          [key]: value,
        },
      },
    });
  };

  const getFormValue = (key) => data.attributes?.form?.[key] || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Tipo de Conta */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <FormControl fullWidth size="small" className="premium-input">
            <InputLabel>{t('sharedAccountType')}</InputLabel>
            <Select
              label={t('sharedAccountType')}
              value={getFormValue('accountType') || ''}
              onChange={(e) => handleFormField('accountType', e.target.value)}
            >
              <MenuItem value="individual">{t('sharedAccountIndividual')}</MenuItem>
              <MenuItem value="company">{t('sharedAccountCompany')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('sharedCPFCNPJ')}
            value={getFormValue('taxId')}
            onChange={(e) => handleFormField('taxId', e.target.value)}
            placeholder="CPF ou CNPJ"
          />
        </Grid>
      </Grid>

      {/* Contato */}
      <Typography
        variant="overline"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('sharedContact') || 'Contato'}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('sharedWhatsapp')}
            value={getFormValue('whatsapp')}
            onChange={(e) => handleFormField('whatsapp', e.target.value)}
            placeholder="+55 11 99999-9999"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('sharedTelegram')}
            value={getFormValue('telegramChatId')}
            onChange={(e) => handleFormField('telegramChatId', e.target.value)}
            placeholder="Chat ID do Telegram"
          />
        </Grid>
      </Grid>

      {/* Endereco */}
      <Typography
        variant="overline"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('sharedAddress')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 8 }} sx={{ minWidth: 220 }}>
          <PremiumInput
            label={t('sharedAddress')}
            value={getFormValue('address1')}
            onChange={(e) => handleFormField('address1', e.target.value)}
            placeholder="Rua, numero"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedNeighborhood')}
            value={getFormValue('address2')}
            onChange={(e) => handleFormField('address2', e.target.value)}
            placeholder="Bairro"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 3 }} sx={{ minWidth: 120 }}>
          <PremiumInput
            label={t('sharedPostalCode')}
            value={getFormValue('postCode')}
            onChange={(e) => handleFormField('postCode', e.target.value)}
            placeholder="00000-000"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('sharedCity')}
            value={getFormValue('city')}
            onChange={(e) => handleFormField('city', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }} sx={{ minWidth: 80 }}>
          <PremiumInput
            label={t('sharedState')}
            value={getFormValue('state')}
            onChange={(e) => handleFormField('state', e.target.value)}
            placeholder="UF"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }} sx={{ minWidth: 120 }}>
          <PremiumInput
            label={t('sharedCountry')}
            value={getFormValue('country')}
            onChange={(e) => handleFormField('country', e.target.value)}
            placeholder="Brasil"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 3: PERMISSOES
// ============================================================================

const PermissionsStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);
  const admin = useAdministrator();
  const manager = useManager();

  const checkboxSx = {
    color: colors.textSecondary,
    '&.Mui-checked': { color: colors.accent },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Limites */}
      <Typography
        variant="overline"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('sharedLimits') || 'Limites'}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('userExpirationTime')}
            value={data.expirationTime ? data.expirationTime.split('T')[0] : '2099-01-01'}
            onChange={(e) => {
              if (e.target.value) {
                updateData({ expirationTime: new Date(e.target.value).toISOString() });
              }
            }}
            type="date"
            disabled={!manager}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('userDeviceLimit')}
            value={data.deviceLimit ?? -1}
            onChange={(e) => updateData({ deviceLimit: Number(e.target.value) })}
            type="number"
            disabled={!admin}
            helperText={t('wizardUnlimitedHint') || '-1 = ilimitado'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 180 }}>
          <PremiumInput
            label={t('userUserLimit')}
            value={data.userLimit ?? 0}
            onChange={(e) => updateData({ userLimit: Number(e.target.value) })}
            type="number"
            disabled={!admin}
            helperText={t('wizardUnlimitedHint') || '-1 = ilimitado'}
          />
        </Grid>
      </Grid>

      {/* Permissoes */}
      <Typography
        variant="overline"
        sx={{
          color: colors.textMuted,
          fontWeight: 600,
          letterSpacing: '0.1em',
          fontSize: '11px',
          pb: 1,
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}
      >
        {t('sharedPermissions')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 1,
          backgroundColor: colors.surfaceHover,
          borderRadius: '8px',
          p: 2,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={data.administrator || false}
              onChange={(e) => updateData({ administrator: e.target.checked })}
              size="small"
              sx={checkboxSx}
            />
          }
          label={<Typography sx={{ color: colors.textPrimary }}>{t('userAdmin')}</Typography>}
          disabled={!admin}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data.disabled || false}
              onChange={(e) => updateData({ disabled: e.target.checked })}
              size="small"
              sx={checkboxSx}
            />
          }
          label={<Typography sx={{ color: colors.textPrimary }}>{t('sharedDisabled')}</Typography>}
          disabled={!manager}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data.readonly || false}
              onChange={(e) => updateData({ readonly: e.target.checked })}
              size="small"
              sx={checkboxSx}
            />
          }
          label={<Typography sx={{ color: colors.textPrimary }}>{t('serverReadonly')}</Typography>}
          disabled={!manager}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data.deviceReadonly || false}
              onChange={(e) => updateData({ deviceReadonly: e.target.checked })}
              size="small"
              sx={checkboxSx}
            />
          }
          label={<Typography sx={{ color: colors.textPrimary }}>{t('userDeviceReadonly')}</Typography>}
          disabled={!manager}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data.limitCommands || false}
              onChange={(e) => updateData({ limitCommands: e.target.checked })}
              size="small"
              sx={checkboxSx}
            />
          }
          label={<Typography sx={{ color: colors.textPrimary }}>{t('userLimitCommands')}</Typography>}
          disabled={!manager}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={data.disableReports || false}
              onChange={(e) => updateData({ disableReports: e.target.checked })}
              size="small"
              sx={checkboxSx}
            />
          }
          label={<Typography sx={{ color: colors.textPrimary }}>{t('userDisableReports')}</Typography>}
          disabled={!manager}
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 4: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const userAttributes = useUserAttributes(t);
  const commonUserAttributes = useCommonUserAttributes(t);

  const allDefinitions = { ...commonUserAttributes, ...userAttributes };

  const handleSetAttributes = (newAttributes) => {
    updateData({ attributes: newAttributes });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <EditAttributesAccordion
        attributes={data.attributes || {}}
        setAttributes={handleSetAttributes}
        definitions={allDefinitions}
      />
    </Box>
  );
};

// ============================================================================
// STEP 5: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: `1px solid ${colors.borderSubtle}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" sx={{ color: colors.textMuted, fontSize: '13px' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: colors.textPrimary,
          fontWeight: 500,
          textAlign: 'right',
          maxWidth: '60%',
          fontSize: '13px',
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  );

  const Section = ({ title, children }) => (
    <Box
      sx={{
        backgroundColor: colors.surfaceHover,
        borderRadius: '8px',
        p: 2,
        border: `1px solid ${colors.borderSubtle}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: colors.accent,
          mb: 2,
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  const getFormValue = (key) => data.attributes?.form?.[key] || '';
  const formatBoolean = (val) => (val ? t('sharedYes') || 'Sim' : t('sharedNo') || 'Nao');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Success banner */}
      <Box
        sx={{
          p: 2.5,
          borderRadius: '10px',
          backgroundColor: colors.accentSubtle,
          border: `1px solid ${colors.accentHover}`,
        }}
      >
        <Typography sx={{ color: colors.accent, fontWeight: 500, fontSize: '14px' }}>
          {t('wizardReviewBanner') || 'Revise os dados antes de salvar'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Dados Basicos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('sharedRequired') || 'Dados Basicos'}>
            <InfoRow label={t('sharedName')} value={data.name} />
            <InfoRow label={t('userEmail')} value={data.email} />
            <InfoRow label={t('sharedPhone')} value={data.phone} />
          </Section>
        </Grid>

        {/* Contato */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('sharedContact') || 'Contato'}>
            <InfoRow label={t('sharedAccountType')} value={getFormValue('accountType')} />
            <InfoRow label={t('sharedCPFCNPJ')} value={getFormValue('taxId')} />
            <InfoRow label={t('sharedWhatsapp')} value={getFormValue('whatsapp')} />
            <InfoRow
              label={t('sharedAddress')}
              value={[getFormValue('address1'), getFormValue('city'), getFormValue('state')].filter(Boolean).join(', ')}
            />
          </Section>
        </Grid>

        {/* Permissoes */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title={t('sharedPermissions')}>
            <InfoRow label={t('userAdmin')} value={formatBoolean(data.administrator)} />
            <InfoRow label={t('sharedDisabled')} value={formatBoolean(data.disabled)} />
            <InfoRow label={t('userDeviceLimit')} value={data.deviceLimit === -1 ? t('sharedUnlimited') || 'Ilimitado' : data.deviceLimit} />
            <InfoRow label={t('userExpirationTime')} value={data.expirationTime?.split('T')[0]} />
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const UserFormWizard = ({ user, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const admin = useAdministrator();
  const currentUser = useSelector((state) => state.session.user);

  const steps = [
    {
      label: t('wizardStepBasic') || 'Basico',
      title: t('wizardBasicTitle') || 'Informacoes Basicas',
      description: t('wizardUserBasicDescription') || 'Preencha os dados essenciais do usuario',
      icon: <PersonIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('wizardErrorRequired') || 'Campo obrigatorio';
        if (!data.email?.trim()) errors.email = t('wizardErrorRequired') || 'Campo obrigatorio';
        if (!data.id && !data.password) errors.password = t('wizardErrorRequired') || 'Campo obrigatorio';
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
        { label: t('userEmail'), value: data.email },
      ],
    },
    {
      label: t('wizardStepContact') || 'Contato',
      title: t('wizardContactTitle') || 'Dados de Contato',
      description: t('wizardContactDescription') || 'Informacoes de contato e endereco',
      icon: <ContactIcon sx={{ fontSize: 18 }} />,
      component: ContactStep,
      optional: true,
    },
    {
      label: t('wizardStepPermissions') || 'Permissoes',
      title: t('wizardPermissionsTitle') || 'Permissoes e Limites',
      description: t('wizardPermissionsDescription') || 'Configure os acessos do usuario',
      icon: <SecurityIcon sx={{ fontSize: 18 }} />,
      component: PermissionsStep,
      optional: true,
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription') || 'Atributos personalizados do usuario',
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview') || 'Revisao',
      title: t('wizardReviewTitle') || 'Revisao Final',
      description: t('wizardReviewDescription') || 'Confirme os dados antes de salvar',
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = user?.id ? `/api/users/${user.id}` : '/api/users';
      const method = user?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const savedUser = await response.json();
        // Update session if editing current user
        if (savedUser.id === currentUser?.id) {
          dispatch(sessionActions.updateUser(savedUser));
        }
        onSave?.(savedUser);
        navigate('/settings/users');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('wizardErrorSave') || 'Erro ao salvar');
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/users');
  };

  const initialData = user || {
    name: '',
    email: '',
    deviceLimit: admin ? -1 : 0,
    attributes: { form: {} },
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={user?.id ? null : 'user-wizard-draft'}
      completeButtonText={user?.id ? (t('wizardSaveChanges') || 'Salvar Alteracoes') : (t('wizardRegisterUser') || 'Cadastrar Usuario')}
      nextButtonText={t('wizardNext') || 'Proximo'}
      backButtonText={t('wizardBack') || 'Voltar'}
      cancelButtonText={t('wizardCancel') || 'Cancelar'}
      skipButtonText={t('wizardSkip') || 'Pular'}
      showSummary
    />
  );
};

export default UserFormWizard;
